import { forwardRef, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@happymoneyinc/styled-components';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import { isTouchEvent } from '../utils/index.js';
import RateReward from './RateReward.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import SvgIcon from '../SvgIcon/SvgIcon.js';

/* eslint-env browser */
const ONE_HUNDRED = 100;
const getRoundUpToMultiple = function (selectedValue) {
  let step = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ONE_HUNDRED;
  return step === ONE_HUNDRED && selectedValue < ONE_HUNDRED / 2 ? 0 : Math.ceil(selectedValue / step) * step;
};
const RangeSlider = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    className,
    disabled,
    dotsPositions,
    height,
    id,
    value,
    max,
    maxRangeValue,
    maxWidth,
    min,
    onChange,
    step,
    rateRewardPosition,
    testID,
    width,
    minLabel,
    maxLabel,
    valueLabel,
    hideRateReward,
    isDangerous,
    ControlInsiderComp,
    LabelComp
  } = props;
  if (step <= 0) throw new Error('"step" property should be a positive number');
  const rangeTrackRef = useRef();
  const delta = max - min;
  const getSelectedValue = useCallback((selectedValue, isInitial) => {
    const selectedValueFromParams = Number(selectedValue);
    let newSelectedValue = Number.isFinite(selectedValueFromParams) ? selectedValueFromParams || min : Number(value) || min;
    if (!isInitial) newSelectedValue = getRoundUpToMultiple(newSelectedValue, step);
    if (newSelectedValue < min) return min;
    if (newSelectedValue > maxRangeValue) return maxRangeValue;
    if (newSelectedValue > max) return max;
    return newSelectedValue;
  }, [max, maxRangeValue, min, step, value]);
  const getSelectedPosition = selectedValue => (selectedValue - min) / delta * ONE_HUNDRED;
  const selectedValue = getSelectedValue(NaN, true);
  const selectedPosition = getSelectedPosition(selectedValue);
  const isReceivingRateReward = rateRewardPosition - selectedPosition <= 0;
  const tracking = useRef(false);
  const fullWidth = useRef(0);
  const clientXStart = useRef(null);
  const sliderStart = useRef(selectedValue);
  const getSelectedValueByClick = (clientX, left, rangeTrackWidth) => {
    const position = (clientX - left) / rangeTrackWidth;
    return Math.round(delta * position + min);
  };
  const onClick = event => {
    var _rangeTrackRef$curren;
    event.stopPropagation();
    if (disabled || tracking !== null && tracking !== void 0 && tracking.current) return;
    const isTouch = isTouchEvent(event);
    const source = isTouch ? (event === null || event === void 0 ? void 0 : event.touches[0]) || (event === null || event === void 0 ? void 0 : event.changedTouches[0]) : event;
    if (!isTouch && event.button !== 0) return;
    const bounding = rangeTrackRef === null || rangeTrackRef === void 0 ? void 0 : (_rangeTrackRef$curren = rangeTrackRef.current) === null || _rangeTrackRef$curren === void 0 ? void 0 : _rangeTrackRef$curren.getBoundingClientRect();
    onChange(getSelectedValue(getSelectedValueByClick(source === null || source === void 0 ? void 0 : source.clientX, bounding === null || bounding === void 0 ? void 0 : bounding.left, bounding === null || bounding === void 0 ? void 0 : bounding.width)));
  };
  const onMouseDown = event => {
    var _rangeTrackRef$curren2;
    if (disabled) return;
    const isTouch = isTouchEvent(event);
    const source = isTouch ? (event === null || event === void 0 ? void 0 : event.touches[0]) || (event === null || event === void 0 ? void 0 : event.changedTouches[0]) : event;
    if (!isTouch && event.button !== 0) return;
    const bounding = rangeTrackRef === null || rangeTrackRef === void 0 ? void 0 : (_rangeTrackRef$curren2 = rangeTrackRef.current) === null || _rangeTrackRef$curren2 === void 0 ? void 0 : _rangeTrackRef$curren2.getBoundingClientRect();
    fullWidth.current = bounding === null || bounding === void 0 ? void 0 : bounding.width;
    tracking.current = true;
    clientXStart.current = source === null || source === void 0 ? void 0 : source.clientX;
    sliderStart.current = getSelectedValueByClick(source === null || source === void 0 ? void 0 : source.clientX, bounding === null || bounding === void 0 ? void 0 : bounding.left, bounding === null || bounding === void 0 ? void 0 : bounding.width);
    onChange(getSelectedValue(sliderStart.current));
  };
  const onMouseMove = useCallback(event => {
    var _rangeTrackRef$curren3;
    if (!(tracking !== null && tracking !== void 0 && tracking.current)) return;
    const source = event !== null && event !== void 0 && event.touches ? event === null || event === void 0 ? void 0 : event.touches[0] : event;
    const bounding = rangeTrackRef === null || rangeTrackRef === void 0 ? void 0 : (_rangeTrackRef$curren3 = rangeTrackRef.current) === null || _rangeTrackRef$curren3 === void 0 ? void 0 : _rangeTrackRef$curren3.getBoundingClientRect();
    const position = ((source === null || source === void 0 ? void 0 : source.clientX) - (bounding === null || bounding === void 0 ? void 0 : bounding.left)) / (bounding === null || bounding === void 0 ? void 0 : bounding.width);
    const newValue = Math.round(delta * position + min);
    onChange(getSelectedValue(newValue));
  }, [getSelectedValue, onChange, delta, min]);
  const cancelTracking = () => {
    tracking.current = false;
  };
  useEffect(() => {
    const options = {
      passive: false
    };
    document.addEventListener('mousemove', onMouseMove, options);
    document.addEventListener('mouseup', cancelTracking, options);
    document.addEventListener('touchend', cancelTracking, options);
    document.addEventListener('touchmove', onMouseMove, options);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cancelTracking);
      document.removeEventListener('touchend', cancelTracking);
      document.removeEventListener('touchmove', onMouseMove);
    };
  }, [onMouseMove]);
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsxs(RangeSliderContainer, {
      ref: ref,
      $maxWidth: maxWidth,
      width: width,
      id: id,
      className: className,
      "data-testid": testID,
      disabled: disabled,
      children: [/*#__PURE__*/jsx(TooltipThumbContainer, {
        $thumbPosition: selectedPosition,
        onMouseDown: onMouseDown,
        onTouchStart: onMouseDown,
        "data-testid": `${testID}-tooltip-thumb`,
        disabled: disabled,
        children: /*#__PURE__*/jsxs(TooltipThumbBox, {
          disabled: disabled,
          children: [/*#__PURE__*/jsx(TooltipThumbItem, {
            disabled: disabled,
            "data-testid": `${testID}-tooltip-thumb-item`,
            $isDangerous: isDangerous,
            children: valueLabel !== null && valueLabel !== void 0 ? valueLabel : `$${selectedValue}`
          }), /*#__PURE__*/jsx(TooltipThumbCorner, {
            type: "corner"
          })]
        })
      }), /*#__PURE__*/jsxs(RangeSliderControl, {
        height: height,
        disabled: disabled,
        onClick: onClick,
        ref: rangeTrackRef,
        onMouseDown: onMouseDown,
        onTouchStart: onMouseDown,
        "data-testid": `${testID}-control`,
        children: [/*#__PURE__*/jsx(RangeSliderFilledLayer, {
          $filledWidth: selectedPosition,
          "data-testid": `${testID}-filled`,
          $isDangerous: isDangerous
        }), /*#__PURE__*/jsx(RangeSliderIndicator, {
          position: selectedPosition,
          $isDangerous: isDangerous
        }), dotsPositions === null || dotsPositions === void 0 ? void 0 : dotsPositions.map((dotPosition, idx) => {
          const isCoveredSelectedPosition = selectedPosition >= dotPosition;
          const keyId = `${testID}-dot-${dotPosition}-${idx}`;
          return /*#__PURE__*/jsx(RangeSliderDotByAllocation, {
            $isCoveredSelectedPosition: isCoveredSelectedPosition,
            $dotPosition: dotPosition,
            "data-testid": keyId
          }, keyId);
        }), ControlInsiderComp && /*#__PURE__*/jsx(ControlInsiderComp, {
          testID: testID
        }), /*#__PURE__*/jsx(RateReward, {
          isReceivingRateReward: isReceivingRateReward,
          rateRewardPosition: rateRewardPosition,
          hideRateReward: hideRateReward
        })]
      }), LabelComp ? /*#__PURE__*/jsx(LabelComp, {
        testID: testID
      }) : /*#__PURE__*/jsxs(MinMaxLabelsContainer, {
        children: [/*#__PURE__*/jsx("div", {
          "data-testid": `${testID}-min`,
          children: minLabel !== null && minLabel !== void 0 ? minLabel : `$${min}`
        }), /*#__PURE__*/jsx("div", {
          "data-testid": `${testID}-max`,
          children: maxLabel !== null && maxLabel !== void 0 ? maxLabel : `$${max}`
        })]
      }), children]
    })
  });
});
const RangeSliderContainer = styled.div`
  position: relative;
  width: ${props => props.width || '100%'};
  max-width: ${prop => prop.$maxWidth || '666px'};
  margin-top: 19px;
  outline: none;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
`;
const RangeSliderControl = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height || '13px'};
  background-color: ${props => {
  var _props$theme, _props$theme$colors;
  return (props === null || props === void 0 ? void 0 : (_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : (_props$theme$colors = _props$theme.colors) === null || _props$theme$colors === void 0 ? void 0 : _props$theme$colors.gray['5']) || '#F3F2F2';
}};
  border-radius: 20px;
  outline: none;
`;
const RangeSliderFilledLayer = styled.div`
  position: absolute;
  width: ${props => (props === null || props === void 0 ? void 0 : props.$filledWidth) || '0'}%;
  height: 100%;
  max-width: 100%;
  background-color: ${_ref => {
  var _theme$colors, _theme$colors2;
  let {
    theme,
    $isDangerous
  } = _ref;
  return $isDangerous ? theme === null || theme === void 0 ? void 0 : (_theme$colors = theme.colors) === null || _theme$colors === void 0 ? void 0 : _theme$colors.systemRed : theme === null || theme === void 0 ? void 0 : (_theme$colors2 = theme.colors) === null || _theme$colors2 === void 0 ? void 0 : _theme$colors2.flamingo['1'];
}};
  border-radius: 20px;
`;
const RangeSliderIndicator = styled.div`
  position: absolute;
  top: -6px;
  left: calc(${props => props.position}% - 12px);
  width: 24px;
  height: 24px;
  background-color: white;
  border: solid 4px ${_ref2 => {
  var _theme$colors3;
  let {
    theme,
    isDangerous
  } = _ref2;
  return isDangerous ? theme === null || theme === void 0 ? void 0 : theme.colors.systemRed : theme === null || theme === void 0 ? void 0 : (_theme$colors3 = theme.colors) === null || _theme$colors3 === void 0 ? void 0 : _theme$colors3.flamingo['1'];
}};
  border-radius: 50%;
`;
const RangeSliderDotByAllocation = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props === null || props === void 0 ? void 0 : props.$dotPosition}%;
  width: 6px;
  height: 6px;
  background-color: ${props => {
  var _props$theme2, _props$theme2$colors, _props$theme3, _props$theme3$colors;
  return props !== null && props !== void 0 && props.$isCoveredSelectedPosition ? props === null || props === void 0 ? void 0 : (_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : (_props$theme2$colors = _props$theme2.colors) === null || _props$theme2$colors === void 0 ? void 0 : _props$theme2$colors.white : props === null || props === void 0 ? void 0 : (_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : (_props$theme3$colors = _props$theme3.colors) === null || _props$theme3$colors === void 0 ? void 0 : _props$theme3$colors.flamingo['1'];
}};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;
const TooltipThumbContainer = styled.div`
  position: absolute;
  top: -42px;
  left: ${props => props.$thumbPosition || 0}%;
  color: ${props => {
  var _props$theme4, _props$theme4$colors;
  return (props === null || props === void 0 ? void 0 : (_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : (_props$theme4$colors = _props$theme4.colors) === null || _props$theme4$colors === void 0 ? void 0 : _props$theme4$colors.white) || '#fff';
}};
  font: normal 700 13px/14px Inter, Serif;
  letter-spacing: 0;
  transform: translateX(-50%);
  cursor: ${props => props.disabled ? 'default' : 'grab'};
  user-select: none;

  &:active {
    cursor: ${props => props.disabled ? 'default' : 'grabbing'};
  }
  ${props => !props.disabled && `
  &:after {
    background: #b5426f;
    content: '';
    display: block;
    height: 6px;
    margin-top: -3px;
    position: absolute;
    right: 4px;
    top: 50%;
    width: 1px;
  }`}
`;
const TooltipThumbBox = styled.div`
  position: relative;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 30px;
  min-width: 80px;
  text-align: center;
  ${props => !props.disabled && `
  &:after {
    background: #b5426f;
    content: '';
    display: block;
    height: 6px;
    left: 4px;
    position: absolute;
    width: 1px;
  }`}
`;
const TooltipThumbItem = styled.div`
  position: relative;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;
  padding: 0 12px;
  background-color: ${_ref3 => {
  var _theme$colors4, _theme$colors5;
  let {
    theme,
    $isDangerous
  } = _ref3;
  return $isDangerous ? theme === null || theme === void 0 ? void 0 : (_theme$colors4 = theme.colors) === null || _theme$colors4 === void 0 ? void 0 : _theme$colors4.systemRed : theme === null || theme === void 0 ? void 0 : (_theme$colors5 = theme.colors) === null || _theme$colors5 === void 0 ? void 0 : _theme$colors5.flamingo['1'];
}};
  border-radius: 40px;
  ${props => !props.disabled && `

  &:before {
    background: #b5426f;
    content: '';
    display: block;
    height: 12px;
    left: 7px;
    position: absolute;
    width: 1px;
  }
  &:after {
    background: #b5426f;
    content: '';
    display: block;
    height: 12px;
    position: absolute;
    right: 7px;
    width: 1px;
  }`}
`;
const TooltipThumbCorner = styled(SvgIcon)`
  position: absolute;
  bottom: -4px;
`;
const MinMaxLabelsContainer = styled.div`
  align-content: baseline;
  justify-content: space-between;
  display: flex;
  margin-top: 10px;
  color: ${props => {
  var _props$theme5;
  return (props === null || props === void 0 ? void 0 : (_props$theme5 = props.theme) === null || _props$theme5 === void 0 ? void 0 : _props$theme5.colors.gray['2']) || '#92908E';
}};
  font: normal 500 12px/13px Inter, Serif;
  letter-spacing: 0;
  user-select: none;
`;
RangeSlider.propTypes = {
  /** Content to be displayed inside the RangeSlider component */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /** Array of numbers this is a allocation tier Of Bonus Dot from left side position in percent */
  dotsPositions: PropTypes.arrayOf(PropTypes.number.isRequired),
  /** Range Slider Height  */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  /** Range Slider maximum value  */
  max: PropTypes.number.isRequired,
  /** Maximum allowable value for selection - filled layer */
  maxRangeValue: PropTypes.number.isRequired,
  /** Maximum allowable width for Range Slider in px ro % */
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Range Slider minimum value  */
  min: PropTypes.number,
  /** Callback function provided for change value (current selected value) */
  onChange: PropTypes.func.isRequired,
  /** One step value for increment or decrement current value by drag or click, by default is equal 100 */
  step: PropTypes.number,
  /** Rate Reward (gift) icon position on the left in percent */
  rateRewardPosition: PropTypes.number,
  /** data-test-id's to handle elements inside the RangeSlider component */
  testID: PropTypes.string,
  /** current selected and init value and this is defines Range Slider filled layer */
  value: PropTypes.number.isRequired,
  /** The default 100% width of the Range Slider in px or %, but not more than the max-width */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Label for the minimum value */
  minLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Label for the maximum value */
  maxLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Value Label to be displayed inside the Range Slider component inside the RangeSliderContainer, by default is equal to current selected value */
  valueLabel: PropTypes.node,
  /** Hide Rate Reward (gift) icon on the left side of the Range Slider */
  hideRateReward: PropTypes.bool,
  /** Display the warning icon */
  isDangerous: PropTypes.bool,
  /** Range Slider control insider component */
  ControlInsiderComp: PropTypes.node,
  /** Label Component */
  LabelComp: PropTypes.node
};
RangeSlider.defaultProps = {
  /** this property will be displayed after the Range Slider component inside the RangeSliderContainer */
  children: null,
  className: '',
  disabled: false,
  /** Array of numbers is left position in percent */
  dotsPositions: [],
  height: '13px',
  id: '',
  maxWidth: '666px',
  min: 0,
  /** one step by onChange */
  step: 100,
  /** Rate Reward (gift) icon position on the left in percent, by default ie equal 95 */
  rateRewardPosition: 95,
  testID: 'range-slider',
  width: '100%',
  minLabel: null,
  maxLabel: null,
  valueLabel: null,
  hideRateReward: false,
  isDangerous: false,
  ControlInsiderComp: null,
  LabelComp: null
};
var RangeSlider$1 = RangeSlider;

export { RangeSlider$1 as default };
//# sourceMappingURL=RangeSlider.js.map
